// Resolves an authored asset path like `images/foo.png` (relative to the blog
// content folder) to its hashed, same-origin build URL. Articles render at a
// deep route (/blog/<slug>), so a raw relative src would 404; and string props
// (e.g. <BeforeAfter before="images/x.png">) are not markdown image nodes, so
// the MDX plugin can't rewrite them. A single eager URL glob handles both.
// Same-origin /assets/<hash> URLs satisfy the CSP `img-src 'self'`.

const assetUrls = import.meta.glob<string>(
  '../../../content/blog/images/*.{png,jpg,jpeg,webp,svg,gif,avif,mp4,webm}',
  { eager: true, query: '?url', import: 'default' },
);

const urlByBasename = new Map<string, string>();
for (const [path, url] of Object.entries(assetUrls)) {
  const base = path.split('/').pop();
  if (base) urlByBasename.set(base, url);
}

/**
 * Resolve `images/foo.png` (or `./images/foo.png`, or a bare `foo.png`) to its
 * built URL. Absolute (`/…`) and remote (`http(s):`, `data:`, `blob:`) srcs are
 * passed through unchanged.
 */
export function resolveAssetUrl(src: string): string {
  if (/^(https?:|data:|blob:|\/)/.test(src)) return src;
  const base = src.split('/').pop() ?? src;
  return urlByBasename.get(base) ?? src;
}
